import { memo, useState } from "react";
import { FiRefreshCcw } from "react-icons/fi";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { FaRegSquareMinus, FaRegSquarePlus } from "react-icons/fa6";
import Shape from "./Shape";
import RangeSlider from "./RangeSlider";
import CommonFilterBoxes from "./CommonFilterBoxes";
import FilterSkeleton from "./FilterSkeleton";

function FiltersPanel({ loading, handleReset, naturalRule, svg }) {
    const [advancedFilters, setAdvancedFilters] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);

    if (loading || !naturalRule) {
        return <FilterSkeleton />;
    }

    const hasAdvanced =
        naturalRule.tablemax ||
        naturalRule.fluorescence ||
        naturalRule.depthmax ||
        naturalRule.polish ||
        naturalRule.symmetry ||
        naturalRule.lab;

    return (
        <div className="w-full px-4">
            <div className="flex flex-wrap items-center">
                <div className="w-full flex justify-between items-center border-b pb-2 mb-4">
                    <span className="font-extrabold text-gray-900 text-2xl leading-loose">
                        Filters
                    </span>
                    <div className="flex items-center">
                        <span className="doc-filter-btn" onClick={handleReset}>
                            <p className="m-0 inline-flex items-center gap-1">
                                <FiRefreshCcw /> Reset
                            </p>
                        </span>
                        {isMinimized ? (
                            <FaRegSquarePlus
                                onClick={() => setIsMinimized(false)}
                                className="text-2xl text-wahchan-primary cursor-pointer ml-2"
                            />
                        ) : (
                            <FaRegSquareMinus
                                onClick={() => setIsMinimized(true)}
                                className="text-2xl text-wahchan-primary cursor-pointer ml-2"
                            />
                        )}
                    </div>
                </div>
            </div>
            {!isMinimized && (
                <>
                    <div className="flex flex-wrap items-start">
                        <div className="w-full md:w-1/2">
                            <Shape
                                name="shape"
                                label="Shape"
                                shapeData={naturalRule.shape?.split(",") ?? []}
                                svg={svg}
                            />
                            <RangeSlider
                                name="price"
                                label="Price"
                                minName="total_price_from"
                                maxName="total_price_to"
                                currency="MYR"
                                min={naturalRule.total_price_from || 0}
                                max={naturalRule.total_price_to || 999999}
                                defaultValue={[
                                    naturalRule.total_price_from || 0,
                                    naturalRule.total_price_to || 999999,
                                ]}
                            />
                            <RangeSlider
                                name="carat"
                                label="Carat"
                                minName="min_carat"
                                maxName="max_carat"
                                min={naturalRule.min_carat || 0}
                                max={naturalRule.max_carat || 100}
                                maxDecimals={2}
                                step="0.01"
                                defaultValue={[
                                    naturalRule.min_carat || 0,
                                    naturalRule.max_carat || 100,
                                ]}
                            />
                            <div
                                className={`advanced-filters-container ${
                                    advancedFilters ? "open" : ""
                                }`}
                            >
                                {naturalRule.tablemax && (
                                    <RangeSlider
                                        name="table"
                                        label="Table"
                                        minName="tablemin"
                                        maxName="tablemax"
                                        min={naturalRule.tablemin || 0}
                                        max={naturalRule.tablemax || 100}
                                        defaultValue={[
                                            naturalRule.tablemin || 0,
                                            naturalRule.tablemax || 100,
                                        ]}
                                    />
                                )}
                                {naturalRule.depthmax && (
                                    <RangeSlider
                                        name="depth"
                                        label="Depth"
                                        minName="depthmin"
                                        maxName="depthmax"
                                        min={naturalRule.depthmin || 0}
                                        max={naturalRule.depthmax || 100}
                                        defaultValue={[
                                            naturalRule.depthmin || 0,
                                            naturalRule.depthmax || 100,
                                        ]}
                                    />
                                )}
                                {naturalRule.lab && (
                                    <CommonFilterBoxes
                                        name="lab"
                                        label="lab"
                                        data={naturalRule.lab.split(",")}
                                        className="clarityBox"
                                    />
                                )}
                            </div>
                        </div>
                        <div className="w-full md:w-1/2">
                            <CommonFilterBoxes
                                name="color"
                                label="Color"
                                data={naturalRule.color?.split(",") ?? []}
                            />
                            {naturalRule.clarity && (
                                <CommonFilterBoxes
                                    name="clarity"
                                    label="Clarity"
                                    data={naturalRule.clarity.split(",")}
                                    className="clarityBox"
                                />
                            )}
                            {naturalRule.cut && (
                                <CommonFilterBoxes
                                    name="cut"
                                    label="Cut"
                                    data={naturalRule.cut.split(",")}
                                    className="clarityBox"
                                />
                            )}
                            <div
                                className={`advanced-filters-container ${
                                    advancedFilters ? "open" : ""
                                }`}
                            >
                                {naturalRule.symmetry && (
                                    <CommonFilterBoxes
                                        name="symmetry"
                                        label="symmetry"
                                        data={naturalRule.symmetry.split(",")}
                                        className="clarityBox"
                                    />
                                )}
                                {naturalRule.fluorescence && (
                                    <CommonFilterBoxes
                                        name="fluorescence"
                                        label="Fluor."
                                        data={naturalRule.fluorescence.split(",")}
                                        className="clarityBox"
                                    />
                                )}
                                {naturalRule.polish && (
                                    <CommonFilterBoxes
                                        name="polish"
                                        label="polish"
                                        data={naturalRule.polish.split(",")}
                                        className="clarityBox"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-center my-4">
                        <button
                            type="submit"
                            className="btn-outline w-full md:w-auto md:px-8"
                        >
                            Apply Filters
                        </button>
                    </div>
                    {hasAdvanced && (
                        <div className="flex border-t">
                            <div className="w-full flex justify-center md:justify-end">
                                <span
                                    className="doc-filter-btn"
                                    onClick={() => setAdvancedFilters((v) => !v)}
                                >
                                    <p className="m-0 inline-flex items-center gap-1">
                                        {advancedFilters ? (
                                            <>
                                                Show Less Filters <IoIosArrowUp />
                                            </>
                                        ) : (
                                            <>
                                                Show More Filters <IoIosArrowDown />
                                            </>
                                        )}
                                    </p>
                                </span>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default memo(FiltersPanel);
